import { useNavigation } from '@react-navigation/native';
import { useFormikContext } from 'formik';
import React from 'react';
import { FlatList, StyleSheet, View, TouchableOpacity } from 'react-native';

import { Text, ShadowBox, Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { MarginBottomXs } from '@components/shared/ListSpacers';
import { useHover } from '@hooks/useHover';
import useNewColors, { ColorType } from '@hooks/useNewColors';

interface ColorPickerProps {
  isBottomSheet?: boolean;
  onSelectFinish?: () => void;
}

const ITEM_HEIGHT = 32;

const getCircleStyles = ({ value }: ColorType) => ({
  width: 16,
  height: 16,
  borderRadius: 8,
  backgroundColor: value,
});

interface ColorPickerItemProps {
  item: ColorType;
  isSelected: boolean;
  onPress: () => void;
}

// TA-10950 #21: Grey01 hover per row, extracted into its own component so
// useHover isn't called inside FlatList's renderItem (Rules of Hooks) -
// mirrors PrioritySelector.web.tsx's PriorityRow.
const ColorPickerItem: React.FC<ColorPickerItemProps> = ({
  item,
  isSelected,
  onPress,
}) => {
  const { name } = item;
  const circleStyles = getCircleStyles(item);
  const { isHovered, hoverProps } = useHover();

  return (
    <TouchableOpacity {...hoverProps} onPress={onPress}>
      <Box
        flexDirection='row'
        alignItems='center'
        paddingVertical='xxs'
        paddingHorizontal='xs'
        borderRadius='xs'
        backgroundColor={isHovered ? 'grey01' : undefined}>
        <Box flex={1} flexDirection='row' alignItems='center'>
          <View accessibilityLabel={name} style={circleStyles} />
          <Text variant='body' color='black' marginLeft='xs'>
            {name}
          </Text>
        </Box>
        {isSelected && <Icon name='Check' color='greenSecondary' variant='s' />}
      </Box>
    </TouchableOpacity>
  );
};

const ColorPickerEmptyList = () => (
  <Text textAlign='center' style={styles.noData}>
    No Data Found
  </Text>
);

const ColorPicker: React.FC<ColorPickerProps> = ({
  isBottomSheet,
  onSelectFinish,
}) => {
  const { colors } = useNewColors();
  const { setFieldValue, getFieldProps, touched, setTouched } =
    useFormikContext();
  const { name: fieldName, value } = getFieldProps('color');
  const navigation = useNavigation();

  const renderItem = ({ item }: { item: ColorType }) => (
    <ColorPickerItem
      item={item}
      isSelected={value == item.id}
      onPress={() => {
        setFieldValue(fieldName, item.id);
        setTouched({ ...touched, [fieldName]: true });
        onSelectFinish ? onSelectFinish() : navigation.pop();
      }}
    />
  );

  const getColorList = () => {
    // TA-10950 #21: rows now carry their own xxs/xs padding so the Grey01
    // hover tint has breathing room instead of hugging the text - these
    // insets are shrunk by that same amount so the list's overall visual
    // spacing (edge insets, gap between rows) is unchanged.
    return (
      <Box paddingVertical='s' paddingHorizontal='xs' borderRadius='xs'>
        <FlatList
          data={colors}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          getItemLayout={(_data, index) => ({
            length: ITEM_HEIGHT,
            offset: ITEM_HEIGHT * index,
            index,
          })}
          ItemSeparatorComponent={MarginBottomXs}
          ListEmptyComponent={ColorPickerEmptyList}
        />
      </Box>
    );
  };

  if (isBottomSheet) {
    return getColorList();
  }

  return (
    <Box flex={1} justifyContent='center' alignItems='center'>
      <TouchableOpacity
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }}
        onPress={() => {
          navigation.goBack();
        }}
        activeOpacity={1}
      />
      <ShadowBox
        variant='thin'
        borderRadius='xs'
        backgroundColor='white'
        style={{ width: 200 }}>
        {getColorList()}
      </ShadowBox>
    </Box>
  );
};

const styles = StyleSheet.create({
  noData: {
    paddingVertical: 45,
  },
});

export default ColorPicker;
